import { token } from '@/infrastructure/container';
import { backoffMs, MAX_ATTEMPTS } from '@/domain/backoff';
import { ConflictError, DomainError, ServerError } from '@/domain/errors';
import { computeTick, type MoveRow as TickRow } from '@/domain/rotation';
import {
  COURTS,
  latestMoveList,
  moveListForTick,
  playerCourt,
  playerRecord,
  sessionNumbering,
  sessionSigma,
  sessionStartedAtMs,
  sessionStatus,
  sessionTolerance,
  type MoveList,
  type NumberingMode,
  type MoveRow,
  type Placement,
  type SessionEvent,
  type SessionState,
  type SessionStatus,
} from '@/domain/session';
import type { IClock } from './Clock';
import { UuidV7Gen, type IIdGen } from './IdGen';
import type { ILogger } from './Logger';
import type { ISleeper } from './Sleeper';

// The admin device's tick engine, pinned by session/contracts/p4-rotation.md (Surface B) and by
// the blind oracle beside this file, which the implementation may not edit.
//
// One calculator, many actioners (docs/rotation.md). Four mover phones each folding the stream at
// slightly different moments and each freezing their own answer would produce contradictory
// instructions with no way to reconcile them, so exactly one device computes the list and
// publishes it, and `moveListComputed`'s precondition (no list for this tick) arbitrates the race
// when a second admin device believes it is that one.
//
// Nothing here is remembered between wakes. Due-ness is `clock.nowMs() - startedAtMs` with the
// stamp read off the FOLD, so an engine that has never seen a start action re-derives the whole
// schedule from the stream. That is the missed-tick rule and it is also what makes this object
// safe to construct twice.

// The gateway shape this engine needs. Identical to ICoachGateway — execute, the folded state, and
// whether the far side is answering — and named separately because the two facades are handed to
// different devices and there is no reason for the admin laptop to be typed as a coach screen.
//
// `execute` here carries the aggregate's OCC events (`sessionStarted`, `moveListComputed`,
// `sessionStopped`) as well as the `occ: false` `playerMoved`. An OCC write resolves only once the
// event has folded, which is what makes the ladder below able to ask "did the list land?" after a
// rejection; a non-OCC write resolves on the ack and folds nothing (docs/sync.md).
export interface ITickGateway {
  execute(event: SessionEvent, opts: { idempotencyKey: string }): Promise<void>;
  readonly sessionState: SessionState; // MobX observable, the current fold
  readonly connectionOk: boolean; // MobX observable
  // WHICH session the fold above belongs to. Optional, and it stays optional: P4's contract pinned
  // this interface and its blind oracle ships a fake implementing it, so a required member here
  // would break a test nobody is allowed to edit. A gateway that does not say reads as "no opinion",
  // which is the behaviour every caller had before this existed.
  //
  // It is here because the admin screen selects a session SYNCHRONOUSLY and opens it
  // asynchronously, so between the two the fold below is still the previous session's. Without this
  // there is no way for a reader to tell (src/views/admin/AdminShellStore.ts).
  readonly sessionId?: string | undefined; // MobX observable
}

export const TOKEN_ITickGateway = token<ITickGateway>('ITickGateway');

// Ten minutes, six of them (docs/session-lifecycle.md, "The clock").
export const TICK_INTERVAL_MS = 10 * 60_000;
export const FINAL_TICK = 6;

// How often the pulse asks whether anything is due. Well under the tick interval, so a tick lands
// within five seconds of its minute, and cheap: a wake on a session with nothing due is two reads
// off the fold and no I/O at all. Nothing depends on the period being exact — due-ness is always
// measured from the clock, never counted in pulses — so a slept laptop that misses a hundred of
// them catches up on the first one after it wakes.
export const PULSE_MS = 5_000;

export class TickEngine {
  // The pulse's teardown, when one is installed. Not observable and not part of the tick's state:
  // the schedule lives in the fold.
  private stopPulse: (() => void) | undefined;
  // One wake at a time. The pulse and a human pressing Start can overlap, and two wakes computing
  // the same tick would race each other into the precondition rather than into the room.
  private waking = false;

  // Three dependencies, and the oracle constructs with exactly these three. The id generator and
  // the sleeper are optional and last, the way CoachStore and AdminAccessStore take theirs, so that
  // construction keeps compiling. The generator defaults to the concrete one because the SDK throws
  // at the wire boundary on an idempotency key that is not a canonical hyphenated UUID
  // (session/scraps.md, the P2 SDK note); with no sleeper the OCC ladder retries immediately, which
  // is what a test wants.
  constructor(
    private readonly gateway: ITickGateway,
    private readonly clock: IClock,
    private readonly logger: ILogger,
    private readonly idGen: IIdGen = new UuidV7Gen(),
    private readonly sleeper?: ISleeper,
  ) {}

  // The admin's start action. Writes `sessionStarted { startedAtMs: clock.nowMs() }` — the admin
  // device's clock, stamped once. The fold keeps the FIRST value it is given and ignores every
  // later one, so a stop and a restart never shift the schedule
  // (docs/session-lifecycle.md, "The clock does not pause").
  //
  // It publishes no list. Minute zero is not a tick, and the pulse it arms is what notices minute
  // ten.
  //
  // Returns whether the session is running: the caller has a button and an error line, and a start
  // that never reached the server is a session nobody has started. The pulse is armed AFTER the
  // write lands rather than before, so a failed start leaves no ticker behind on a screen that
  // says the session did not start. Nothing regresses from the move — selecting a session already
  // arms the pulse (AdminShellStore.openSelected), and this arms it again for the device that
  // pressed the button.
  async start(): Promise<boolean> {
    const started = await this.writeGuarded(
      { kind: 'sessionStarted', payload: { startedAtMs: this.clock.nowMs() } },
      () => sessionStatus(this.gateway.sessionState) === 'running',
    );
    if (started) this.startTicking();
    return started;
  }

  // The engine's pulse. Publishes every tick that is due and does not have a list yet, in
  // ascending order, and does nothing at all unless the session is running. Due-ness is
  // `clock.nowMs() - startedAtMs`, read off the FOLD rather than off a field this object
  // remembered: a laptop that was asleep at the hour mark, or one that opened the app late, has
  // no memory to consult and must re-derive from the stream.
  //
  // Never rejects. Every call site is a pulse or a `void` from a view, where a rejection would
  // reach no catch and no boundary (INVARIANTS rule 17).
  async wake(): Promise<void> {
    if (this.waking) return;
    this.waking = true;
    try {
      for (const tick of this.dueTicks()) {
        const landed = await this.publish(tick);
        // The tick is still due and still has no list, so the next wake will try it again. What it
        // must not do is run AHEAD: a tick 2 published while tick 1 is missing would be a list
        // computed from a room the previous list never reached.
        if (!landed) return;
      }
      // The close-out is a condition on the FOLD, not a branch off the tick this wake happened to
      // publish. A peer admin that landed the final list and then went flat leaves a session whose
      // hour is up, whose last list exists, and which nothing would ever stop if only the device
      // that computed it were allowed to close it. Re-running is cheap and inert: `session.ts`
      // move() is a no-op once a player is on the destination court, the rows already there are
      // skipped below, and `writeGuarded`'s landed predicate is satisfied the moment the status
      // leaves `running`.
      if (this.hourIsUp()) await this.closeOut();
    } catch (cause) {
      // Nothing below is expected to throw — every write is wrapped — so reaching here is a bug in
      // this file or in something it called. It is still swallowed: the alternative is an unhandled
      // rejection out of a timer that nobody sees.
      this.logger.error('tick engine: wake failed', describe(cause));
    } finally {
      this.waking = false;
    }
  }

  // Arms the pulse. Idempotent, and deliberately NOT done in the constructor: every device in the
  // building resolves the graph this engine is registered in, and a coach's phone with a running
  // pulse is a second calculator. The admin screen is the only caller (AdminShellStore), so the
  // laptop that opened a session is the only device that computes its ticks.
  startTicking(): void {
    if (this.stopPulse !== undefined) return;
    this.stopPulse = this.clock.startTicker(PULSE_MS, () => void this.wake());
  }

  // Tears down the ticker. Idempotent.
  dispose(): void {
    this.stopPulse?.();
    this.stopPulse = undefined;
  }

  // --- what the admin screen reads ------------------------------------------------------------
  // All derived from the fold and the clock, so they are as current as the caller's own read.

  /** The outlier constant in force, as the fold holds it. */
  get sigma(): number {
    return sessionSigma(this.gateway.sessionState);
  }

  /** The rebalance tolerance in force. */
  get tolerance(): number {
    return sessionTolerance(this.gateway.sessionState);
  }

  /**
   * Where this session's player numbers come from. Nothing about the TICK depends on it; it is here
   * because this is the object holding the admin device's open session fold, which is the same
   * reason the two knobs above are here. Reading it off the fold rather than off the admin store is
   * what makes the screen show what the HALL is working under rather than what this laptop last
   * clicked (docs/roster.md).
   */
  get numbering(): NumberingMode {
    return sessionNumbering(this.gateway.sessionState);
  }

  /**
   * Which session the fold behind all of the above belongs to, or undefined from a gateway that does
   * not say. The admin screen compares it against the row the human selected.
   */
  get openSessionId(): string | undefined {
    return this.gateway.sessionId;
  }

  /** Milliseconds since the ACTUAL start, or undefined for a session with no stamp. */
  elapsedMs(nowMs: number): number | undefined {
    const startedAt = sessionStartedAtMs(this.gateway.sessionState);
    return startedAt === undefined ? undefined : nowMs - startedAt;
  }

  /**
   * The tick number the hall is waiting for, 1..6, or undefined once the last one has landed.
   *
   * Also undefined for a NEGATIVE elapsed time, which is a session whose start stamp is in this
   * device's future: an admin laptop that NTP dragged backwards, or a second laptop whose clock is
   * behind the one that stamped the start. The panel would otherwise read "tick -5 in 10 min",
   * which is not a countdown to anything. This is cosmetic and only cosmetic — `dueTicks` has its
   * own arithmetic and computes nothing in that state either way, and the real skewed-clock
   * question is a P6 item in session/scraps.md.
   */
  nextTick(nowMs: number): number | undefined {
    const elapsed = this.elapsedMs(nowMs);
    if (elapsed === undefined || elapsed < 0) return undefined;
    const next = Math.floor(elapsed / TICK_INTERVAL_MS) + 1;
    return next > FINAL_TICK ? undefined : next;
  }

  /** How long until that tick is due. Never negative: a tick that is late is due now. */
  nextTickInMs(nowMs: number): number | undefined {
    const elapsed = this.elapsedMs(nowMs);
    const next = this.nextTick(nowMs);
    if (elapsed === undefined || next === undefined) return undefined;
    return Math.max(0, next * TICK_INTERVAL_MS - elapsed);
  }

  /** The newest list the admin has published or seen a peer publish. */
  get moveList(): MoveList | undefined {
    return latestMoveList(this.gateway.sessionState);
  }

  /** The fold's status for the open session, which is what gates the admin's Start/Stop pair. */
  get status(): SessionStatus {
    return sessionStatus(this.gateway.sessionState);
  }

  /**
   * What the next tick WOULD publish if it ran on the fold as it stands — the same pure
   * `computeTick` the wake runs, on the same state, reasons kept rather than stripped. Derived and
   * never written, so it moves live as votes land and the knobs turn: this is what lets the admin
   * tweak sigma BEFORE a rotation instead of reading the list it already published. It is a
   * forecast, not a promise — every vote between now and the wake can change it.
   */
  get previewMoves(): readonly TickRow[] {
    const state = this.gateway.sessionState;
    return computeTick(state, {
      sigma: sessionSigma(state),
      tolerance: sessionTolerance(state),
      // Unused by the arithmetic (the tick's steps do not depend on which tick it is; tick 6's
      // bottom/top placement applies at close-out); passed for the contract shape.
      tickIndex: 0,
    });
  }

  /**
   * The rows of the newest list nobody has actioned yet, read off the fold with the same rule the
   * mover screen uses: a row is outstanding while its player is still standing on the row's
   * from-court, and a player who was moved (by any device) or went home is dealt with. This is
   * what the admin panel shows instead of the full list — the admin's question is "is the walking
   * done", not "what was computed".
   */
  get outstandingMoves(): readonly MoveRow[] {
    const list = latestMoveList(this.gateway.sessionState);
    if (list === undefined) return [];
    return list.moves.filter((row) => {
      // By number, not by index: a pre-allocated day's `players` is sorted by number and has gaps
      // in it, so `players[n - 1]` is somebody else (docs/roster.md).
      const record = playerRecord(this.gateway.sessionState, row.player);
      return record !== undefined && !record.struckOff && record.court === row.from;
    });
  }

  // --- internals ------------------------------------------------------------------------------

  // Every tick whose minute has passed and which has no list yet, ascending. A session that is not
  // running has none: the clock keeps going underneath a stop, and the ticks it passed are still
  // missed ticks, but nothing is computed until the admin starts it again
  // (docs/session-lifecycle.md).
  private dueTicks(): readonly number[] {
    const state = this.gateway.sessionState;
    if (sessionStatus(state) !== 'running') return [];
    const startedAt = sessionStartedAtMs(state);
    // A session started by a device that stamped no time. There is no schedule to derive, and
    // inventing one would decide on this device's own authority that the hour is up.
    if (startedAt === undefined) return [];
    const elapsed = this.clock.nowMs() - startedAt;
    const due = Math.min(FINAL_TICK, Math.floor(elapsed / TICK_INTERVAL_MS));
    const ticks: number[] = [];
    for (let tick = 1; tick <= due; tick++) {
      if (moveListForTick(state, tick) === undefined) ticks.push(tick);
    }
    return ticks;
  }

  // Whether this session is one the close-out applies to: still running, an hour past its stamped
  // start, and holding a final list from SOMEBODY. All three off the fold and the clock, so a
  // laptop that opened the app at minute 75 answers the same as the one that ran the hour.
  private hourIsUp(): boolean {
    const state = this.gateway.sessionState;
    if (sessionStatus(state) !== 'running') return false;
    const elapsed = this.elapsedMs(this.clock.nowMs());
    if (elapsed === undefined || elapsed < FINAL_TICK * TICK_INTERVAL_MS) return false;
    return moveListForTick(state, FINAL_TICK) !== undefined;
  }

  // Computes one tick and publishes it. Returns whether there is now a list for that tick, from
  // any device: a peer that got there first is a success, not a failure.
  //
  // An EMPTY list is still published. A tick that wrote nothing would have no `moveListComputed`
  // for its index and would be re-derived as a missed tick on every wake for the rest of the hour.
  private async publish(tick: number): Promise<boolean> {
    const state = this.gateway.sessionState;
    const rows = computeTick(state, {
      sigma: sessionSigma(state),
      tolerance: sessionTolerance(state),
      tickIndex: tick,
    });
    this.logger.debug(`tick ${tick}: ${rows.length} moves`);
    return this.writeGuarded(
      // The reason a row is in the list is a working value inside the tick and is stripped here:
      // the shipped payload carries `{ player, from, to }` and nothing else.
      { kind: 'moveListComputed', payload: { tick, moves: rows.map(stripReason) } },
      () => moveListForTick(this.gateway.sessionState, tick) !== undefined,
    );
  }

  // The 60-minute tick, once its list has landed. The moves apply from here with no mover walking
  // anyone anywhere — nobody plays after this — and then the session stops itself
  // (docs/session-lifecycle.md, "The clock").
  //
  // The rows come from the FOLD rather than from what this device computed a moment ago, because a
  // peer may have won the race for the final list and the hall is walking that one.
  //
  // Re-entrant by design, because a `sessionStopped` that keeps being refused leaves the hour up
  // and the close-out due on the next pulse. A row whose player is already standing on the
  // destination is skipped: the write would be a no-op in the fold either way, and a pending stop
  // must not restage the whole list as a burst every five seconds.
  private async closeOut(): Promise<void> {
    const rows = moveListForTick(this.gateway.sessionState, FINAL_TICK)?.moves ?? [];
    for (const row of rows) {
      if (playerCourt(this.gateway.sessionState, row.player) === row.to) continue;
      // `occ: false`, so one execute each with a fresh key and no ladder — there is nothing to
      // climb. A row that fails is logged and the rest still apply: a half-applied final list is
      // better than an unapplied one, and the alternative is a session that never stops.
      await this.writeOnce({
        kind: 'playerMoved',
        payload: { player: row.player, toCourt: row.to, placement: placementFor(row.from, row.to) },
      });
    }
    await this.writeGuarded({ kind: 'sessionStopped', payload: {} }, () => {
      return sessionStatus(this.gateway.sessionState) !== 'running';
    });
  }

  // The OCC ladder: three attempts, ONE idempotency key across all of them, deterministic jitter
  // between. A retry that regenerated the key would lose its dedup, and a write that landed without
  // acking would become a second list for the same tick.
  //
  // `landed` is what a rejection is checked against. A ConflictError means the aggregate moved, and
  // the question that matters is whether it moved because a PEER did the same thing: if the fold
  // now holds the list, re-sending is how a second list gets written, and telling the admin the
  // tick failed would be a lie.
  private async writeGuarded(event: SessionEvent, landed: () => boolean): Promise<boolean> {
    const idempotencyKey = this.idGen.next();
    for (let attempt = 1; ; attempt++) {
      try {
        await this.gateway.execute(event, { idempotencyKey });
        return true;
      } catch (cause) {
        const failure = translateFailure(cause, event.kind);
        if (landed()) {
          this.logger.debug(`tick engine: ${event.kind} lost the race, a peer landed it`);
          return true;
        }
        if (!(failure instanceof ConflictError) || attempt >= MAX_ATTEMPTS) {
          this.logger.warn(`tick engine: ${event.kind} failed on attempt ${attempt}`, failure.tag);
          return false;
        }
        this.logger.debug(`tick engine: OCC clash on ${event.kind}, retrying (attempt ${attempt})`);
        if (this.sleeper !== undefined) {
          await this.sleeper.sleep(backoffMs(idempotencyKey, attempt));
        }
      }
    }
  }

  // One `occ: false` write, no ladder. It cannot conflict on a version it never sent, and
  // docs/sync.md forbids queues and replays.
  private async writeOnce(event: SessionEvent): Promise<void> {
    try {
      await this.gateway.execute(event, { idempotencyKey: this.idGen.next() });
    } catch (cause) {
      const failure = translateFailure(cause, event.kind);
      this.logger.warn(`tick engine: ${event.kind} failed`, failure.tag);
    }
  }
}

// Where a player lands at the whistle. Up-movers go to the BOTTOM of the destination and
// down-movers to the TOP: nobody plays after this tick, so the placement decides where they sit in
// the final report, and a player promoted at the whistle has not earned a mid-table slot in their
// new division (docs/rotation-algorithm.md, "The 60-minute tick").
function placementFor(from: TickRow['from'], to: TickRow['to']): Placement {
  return COURTS.indexOf(to) > COURTS.indexOf(from) ? 'bottom' : 'top';
}

function stripReason(row: { player: number; from: TickRow['from']; to: TickRow['to'] }) {
  return { player: row.player, from: row.from, to: row.to };
}

// The gateway translates at the SDK boundary, so what arrives here is already in the taxonomy —
// unless it is a bug inside the gateway, which arrives as a TypeError and must not be absorbed as
// if it were an outage (INVARIANTS rule 17).
function translateFailure(cause: unknown, what: string): DomainError {
  return cause instanceof DomainError ? cause : new ServerError(`${what} failed`, { cause });
}

function describe(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

export const TOKEN_TickEngine = token<TickEngine>('TickEngine');
